import type { SubscriberRow } from '../components/Subscribers/types';
import { subscriberEmailStatus } from './subscriberEmailStatus';
import { supabase } from './supabase';

export type StatusCountSummary = {
  total: number;
  last7Days: number;
};

export type ConnectionTrendPoint = {
  label: string;
  periodStart: string;
  /** Connections added on this day (grouped by created_at date) */
  added: number;
  /** Monday — draw a week divider line */
  isWeekStart: boolean;
  /** Day is after today (empty slot) */
  isFuture: boolean;
};

export type CumulativeSubscriberTrendPoint = {
  label: string;
  periodStart: string;
  /** Running total subscribers through end of this day */
  total: number;
  /** Subscribers added on this day */
  added: number;
  isWeekStart: boolean;
  /** Day is after today (empty slot) */
  isFuture: boolean;
};

export const CONNECTION_TREND_DAY_OPTIONS = [1, 6, 7, 14, 30, 60, 90] as const;
export type ConnectionTrendDayOption = (typeof CONNECTION_TREND_DAY_OPTIONS)[number];
export const DEFAULT_CONNECTION_TREND_DAYS: ConnectionTrendDayOption = 7;
export const ONE_HOUR_TREND_RANGE = 1;
export const SIX_HOUR_TREND_RANGE = 6;
export const WEEKLY_TREND_DAY_COUNTS: readonly ConnectionTrendDayOption[] = [60, 90];
export const FIVE_MINUTE_BUCKET_MINUTES = 5;
export const ONE_HOUR_BUCKET_COUNT = 12;

export type TrendBucketMode = '5min' | 'hourly' | 'daily' | 'weekly';

export function usesShortHourlyTrend(range: number): boolean {
  return range === ONE_HOUR_TREND_RANGE || range === SIX_HOUR_TREND_RANGE;
}

export function usesWeeklyTrendBuckets(dayCount: number): boolean {
  return (WEEKLY_TREND_DAY_COUNTS as readonly number[]).includes(dayCount);
}

export function getTrendBucketMode(dayCount: number): TrendBucketMode {
  if (dayCount === ONE_HOUR_TREND_RANGE) return '5min';
  if (dayCount === SIX_HOUR_TREND_RANGE) return 'hourly';
  return usesWeeklyTrendBuckets(dayCount) ? 'weekly' : 'daily';
}

export function formatDashboardRangeOption(range: ConnectionTrendDayOption): string {
  if (range === ONE_HOUR_TREND_RANGE) return 'Last 1 hour';
  if (range === SIX_HOUR_TREND_RANGE) return 'Last 6 hours';
  return `${range} days${usesWeeklyTrendBuckets(range) ? ' · weekly' : ''}`;
}

export function formatTrendRangeSummary(range: ConnectionTrendDayOption): string {
  if (range === ONE_HOUR_TREND_RANGE) return '1h';
  if (range === SIX_HOUR_TREND_RANGE) return '6h';
  return `${range}d`;
}

export function getTrendChartUnitLabel(range: ConnectionTrendDayOption): string | null {
  if (range === ONE_HOUR_TREND_RANGE || range === SIX_HOUR_TREND_RANGE) return 'Hours';
  if (usesWeeklyTrendBuckets(range)) return 'Weeks';
  return 'Days';
}

function parseLocalPeriodStart(periodStart: string): Date | null {
  const hourMatch = periodStart.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2})$/);
  if (hourMatch) {
    const [, y, m, d, hour] = hourMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hour), 0, 0, 0);
  }

  const fiveMinMatch = periodStart.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (fiveMinMatch) {
    const [, y, m, d, hour, minute] = fiveMinMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hour), Number(minute), 0, 0);
  }

  const dayMatch = periodStart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dayMatch) {
    const [, y, m, d] = dayMatch;
    return new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0, 0);
  }

  return null;
}

/** X-axis endpoint label — hours when in short hourly ranges. */
export function formatChartAxisLabel(periodStart: string, bucketMode: TrendBucketMode): string {
  if (bucketMode === 'hourly' || bucketMode === '5min') {
    const at = parseLocalPeriodStart(periodStart);
    if (!at) return periodStart;
    return at.toLocaleTimeString(undefined, { hour: 'numeric' });
  }
  if (bucketMode === 'weekly') return formatWeekRangeLabel(periodStart);
  const at = parseLocalPeriodStart(periodStart);
  if (!at) return periodStart;
  return at.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function getChartAxisEndpointLabels<T extends { periodStart: string; label: string; isFuture?: boolean }>(
  points: T[],
  bucketMode: TrendBucketMode,
): { start: string; end: string } {
  const realPoints = points.filter((point) => !point.isFuture);
  const startPoint = realPoints[0] ?? points[0];
  const endPoint = realPoints[realPoints.length - 1] ?? points[points.length - 1];
  if (!startPoint || !endPoint) return { start: '', end: '' };

  if (bucketMode === 'hourly' || bucketMode === '5min') {
    return {
      start: formatChartAxisLabel(startPoint.periodStart, bucketMode),
      end: formatChartAxisLabel(endPoint.periodStart, bucketMode),
    };
  }

  return { start: startPoint.label, end: endPoint.label };
}

export type ConnectionDashboardData = {
  total: number;
  addedLast7Days: number;
  confirmed: StatusCountSummary;
  recent: SubscriberRow[];
  createdAtValues: string[];
};

type SubscriberDashboardRow = {
  id?: string | number;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  profile_score?: number | null;
  profile_score_tier?: SubscriberRow['profile_score_tier'];
  created_at?: string | null;
  email_status?: string | null;
  status?: string | null;
};

function startOfLocalDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function startOfLocalHour(d: Date): Date {
  const copy = new Date(d);
  copy.setMinutes(0, 0, 0);
  return copy;
}

function startOfLocalMinuteInterval(d: Date, intervalMinutes: number): Date {
  const copy = startOfLocalHour(d);
  const flooredMinutes = Math.floor(d.getMinutes() / intervalMinutes) * intervalMinutes;
  copy.setMinutes(flooredMinutes);
  return copy;
}

/** Calendar day key in local time (YYYY-MM-DD). */
export function createdAtDayKey(raw: string | null | undefined): string {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

export function localFiveMinKeyFromDate(d: Date): string {
  const bucket = startOfLocalMinuteInterval(d, FIVE_MINUTE_BUCKET_MINUTES);
  const y = bucket.getFullYear();
  const m = String(bucket.getMonth() + 1).padStart(2, '0');
  const day = String(bucket.getDate()).padStart(2, '0');
  const hour = String(bucket.getHours()).padStart(2, '0');
  const minute = String(bucket.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hour}:${minute}`;
}

export function localHourKeyFromDate(d: Date): string {
  const bucket = startOfLocalHour(d);
  const y = bucket.getFullYear();
  const m = String(bucket.getMonth() + 1).padStart(2, '0');
  const day = String(bucket.getDate()).padStart(2, '0');
  const hour = String(bucket.getHours()).padStart(2, '0');
  return `${y}-${m}-${day}T${hour}`;
}

export function createdAtFiveMinKey(raw: string | null | undefined): string {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  return localFiveMinKeyFromDate(parsed);
}

export function createdAtHourKey(raw: string | null | undefined): string {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  return localHourKeyFromDate(parsed);
}

function formatFiveMinLabel(key: string): string {
  const at = parseLocalPeriodStart(key);
  if (!at) return key;
  return at.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatHourLabel(key: string): string {
  const at = parseLocalPeriodStart(key);
  if (!at) return key;
  return at.toLocaleTimeString(undefined, { hour: 'numeric' });
}

export function formatFiveMinTooltip(key: string): string {
  const at = parseLocalPeriodStart(key);
  if (!at) return key;
  return at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatHourTooltip(key: string): string {
  const at = parseLocalPeriodStart(key);
  if (!at) return key;
  return at.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatTrendPeriodTooltip(periodStart: string, bucketMode: TrendBucketMode): string {
  if (bucketMode === 'weekly') return formatWeekRangeTooltip(periodStart);
  if (bucketMode === '5min') return formatFiveMinTooltip(periodStart);
  if (bucketMode === 'hourly') return formatHourTooltip(periodStart);
  const d = new Date(`${periodStart}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

type TrendTimeBucket = {
  key: string;
  label: string;
  isFuture: boolean;
  isDivider: boolean;
};

export function buildFiveMinTrendBuckets(): TrendTimeBucket[] {
  const now = new Date();
  const currentStart = startOfLocalMinuteInterval(now, FIVE_MINUTE_BUCKET_MINUTES);
  const currentKey = localFiveMinKeyFromDate(currentStart);
  const intervalMs = FIVE_MINUTE_BUCKET_MINUTES * 60 * 1000;
  const buckets: TrendTimeBucket[] = [];

  for (let i = ONE_HOUR_BUCKET_COUNT - 1; i >= -1; i--) {
    const d = new Date(currentStart.getTime() - i * intervalMs);
    const key = localFiveMinKeyFromDate(d);
    buckets.push({
      key,
      label: formatFiveMinLabel(key),
      isFuture: key > currentKey,
      isDivider: key.endsWith(':00'),
    });
  }

  return buckets;
}

export function buildHourlyTrendBuckets(hours: number): TrendTimeBucket[] {
  const now = new Date();
  const currentHour = startOfLocalHour(now);
  const currentKey = localHourKeyFromDate(currentHour);
  const buckets: TrendTimeBucket[] = [];

  for (let i = hours - 1; i >= -1; i--) {
    const d = new Date(currentHour);
    d.setHours(d.getHours() - i);
    const key = localHourKeyFromDate(d);
    buckets.push({
      key,
      label: formatHourLabel(key),
      isFuture: key > currentKey,
      isDivider: key.endsWith('T00'),
    });
  }

  return buckets;
}

function formatDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Monday (local) for the week containing isoDate (YYYY-MM-DD). */
export function mondayWeekKey(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  const weekday = d.getDay();
  const diff = weekday === 0 ? -6 : 1 - weekday;
  d.setDate(d.getDate() + diff);
  return localDayKeyFromDate(d);
}

export function formatWeekRangeLabel(weekStartKey: string): string {
  const start = new Date(`${weekStartKey}T12:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const endLabel = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${startLabel}–${endLabel}`;
}

export function formatWeekRangeTooltip(weekStartKey: string): string {
  const start = new Date(`${weekStartKey}T12:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`;
}

function isMonday(isoDate: string): boolean {
  return new Date(`${isoDate}T12:00:00`).getDay() === 1;
}

/** Inclusive calendar-day window: today and the prior (days - 1) days. */
export function startOfTrendWindow(days: number): Date {
  const start = startOfLocalDay(new Date());
  start.setDate(start.getDate() - (days - 1));
  return start;
}

export function isCreatedOnOrAfterDay(raw: string, windowStart: Date): boolean {
  const key = createdAtDayKey(raw);
  if (!key) return false;
  const createdDay = new Date(`${key}T12:00:00`);
  const startDay = startOfLocalDay(windowStart);
  return createdDay >= startDay;
}

function summarizeByEmailStatus(
  rows: SubscriberDashboardRow[],
  windowStart: Date,
  targetStatus: 'confirmed' | 'pending',
): StatusCountSummary {
  let total = 0;
  let last7Days = 0;

  for (const row of rows) {
    const status = subscriberEmailStatus(row).toLowerCase();
    if (status !== targetStatus) continue;
    total += 1;
    const created = row.created_at;
    if (typeof created === 'string' && isCreatedOnOrAfterDay(created, windowStart)) {
      last7Days += 1;
    }
  }

  return { total, last7Days };
}

/** Last N calendar days through tomorrow, oldest → newest. */
export function buildDailyConnectionTrend(
  createdAtValues: string[],
  dayCount: number = DEFAULT_CONNECTION_TREND_DAYS,
): ConnectionTrendPoint[] {
  const now = new Date();
  const todayKey = localDayKeyFromDate(startOfLocalDay(now));
  const buckets: { key: string; label: string; added: number; isFuture: boolean }[] = [];

  // dayCount days of history ending today, plus 1 day into the future
  for (let i = dayCount - 1; i >= -1; i--) {
    const d = startOfLocalDay(now);
    d.setDate(d.getDate() - i);
    const key = localDayKeyFromDate(d);
    buckets.push({
      key,
      label: formatDayLabel(key),
      added: 0,
      isFuture: key > todayKey,
    });
  }

  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  for (const raw of createdAtValues) {
    const key = createdAtDayKey(raw);
    if (!key) continue;
    const idx = bucketIndex.get(key);
    if (idx != null) buckets[idx].added += 1;
  }

  return buckets.map((b) => ({
    label: b.label,
    periodStart: b.key,
    added: b.added,
    isWeekStart: isMonday(b.key),
    isFuture: b.isFuture,
  }));
}

function buildFiveMinConnectionTrend(createdAtValues: string[]): ConnectionTrendPoint[] {
  const buckets = buildFiveMinTrendBuckets();
  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));
  const adds = buckets.map(() => 0);

  for (const raw of createdAtValues) {
    const key = createdAtFiveMinKey(raw);
    if (!key) continue;
    const idx = bucketIndex.get(key);
    if (idx == null || buckets[idx].isFuture) continue;
    adds[idx] += 1;
  }

  return buckets.map((b, i) => ({
    label: b.label,
    periodStart: b.key,
    added: adds[i],
    isWeekStart: b.isDivider,
    isFuture: b.isFuture,
  }));
}

function buildHourlyConnectionTrend(createdAtValues: string[], hours: number): ConnectionTrendPoint[] {
  const buckets = buildHourlyTrendBuckets(hours).map((b) => ({ ...b, added: 0 }));
  const bucketIndex = new Map(buckets.map((b, i) => [b.key, i]));

  for (const raw of createdAtValues) {
    const key = createdAtHourKey(raw);
    if (!key) continue;
    const idx = bucketIndex.get(key);
    if (idx == null) continue;
    if (buckets[idx].isFuture) continue;
    buckets[idx].added += 1;
  }

  return buckets.map((b) => ({
    label: b.label,
    periodStart: b.key,
    added: b.added,
    isWeekStart: b.isDivider,
    isFuture: b.isFuture,
  }));
}

function aggregateConnectionTrendByWeek(daily: ConnectionTrendPoint[]): ConnectionTrendPoint[] {
  const weeks = new Map<string, number>();

  for (const point of daily) {
    if (point.isFuture) continue;
    const weekKey = mondayWeekKey(point.periodStart);
    weeks.set(weekKey, (weeks.get(weekKey) ?? 0) + point.added);
  }

  return [...weeks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekKey, added]) => ({
      label: formatWeekRangeLabel(weekKey),
      periodStart: weekKey,
      added,
      isWeekStart: true,
      isFuture: false,
    }));
}

/** Short hourly, daily, or weekly buckets depending on range. */
export function buildConnectionTrend(
  createdAtValues: string[],
  dayCount: number = DEFAULT_CONNECTION_TREND_DAYS,
): ConnectionTrendPoint[] {
  if (dayCount === ONE_HOUR_TREND_RANGE) {
    return buildFiveMinConnectionTrend(createdAtValues);
  }
  if (dayCount === SIX_HOUR_TREND_RANGE) {
    return buildHourlyConnectionTrend(createdAtValues, SIX_HOUR_TREND_RANGE);
  }
  const daily = buildDailyConnectionTrend(createdAtValues, dayCount);
  if (!usesWeeklyTrendBuckets(dayCount)) return daily;
  return aggregateConnectionTrendByWeek(daily);
}

/** Cumulative subscriber total for each period in the window. */
export function buildCumulativeSubscriberTrend(
  createdAtValues: string[],
  dayCount: number = DEFAULT_CONNECTION_TREND_DAYS,
): CumulativeSubscriberTrendPoint[] {
  if (dayCount === ONE_HOUR_TREND_RANGE) {
    return buildFiveMinCumulativeSubscriberTrend(createdAtValues);
  }
  if (dayCount === SIX_HOUR_TREND_RANGE) {
    return buildHourlyCumulativeSubscriberTrend(createdAtValues, SIX_HOUR_TREND_RANGE);
  }
  const daily = buildDailyCumulativeSubscriberTrend(createdAtValues, dayCount);
  if (!usesWeeklyTrendBuckets(dayCount)) return daily;
  return aggregateCumulativeSubscriberByWeek(daily);
}

function buildFiveMinCumulativeSubscriberTrend(
  createdAtValues: string[],
): CumulativeSubscriberTrendPoint[] {
  const buckets = buildFiveMinTrendBuckets();
  const windowStartKey = buckets[0]?.key ?? '';
  const currentKey = [...buckets].reverse().find((b) => !b.isFuture)?.key ?? '';

  let runningTotal = 0;
  const periodAdds = buckets.map(() => 0);

  for (const raw of createdAtValues) {
    const key = createdAtFiveMinKey(raw);
    if (!key) continue;
    if (key < windowStartKey) {
      runningTotal += 1;
    } else if (key <= currentKey) {
      const idx = buckets.findIndex((b) => b.key === key);
      if (idx >= 0) periodAdds[idx] += 1;
    }
  }

  return buckets.map((b, i) => {
    const added = b.isFuture ? 0 : periodAdds[i];
    if (!b.isFuture) runningTotal += added;
    return {
      label: b.label,
      periodStart: b.key,
      total: runningTotal,
      added,
      isWeekStart: b.isDivider,
      isFuture: b.isFuture,
    };
  });
}

function buildHourlyCumulativeSubscriberTrend(
  createdAtValues: string[],
  hours: number,
): CumulativeSubscriberTrendPoint[] {
  const buckets = buildHourlyTrendBuckets(hours);
  const windowStartKey = buckets[0]?.key ?? '';
  let currentKey = '';
  for (const bucket of buckets) {
    if (!bucket.isFuture) currentKey = bucket.key;
  }

  let runningTotal = 0;
  const periodAdds = buckets.map(() => 0);

  for (const raw of createdAtValues) {
    const key = createdAtHourKey(raw);
    if (!key) continue;
    if (key < windowStartKey) {
      runningTotal += 1;
    } else if (key <= currentKey) {
      const idx = buckets.findIndex((b) => b.key === key);
      if (idx >= 0) periodAdds[idx] += 1;
    }
  }

  return buckets.map((b, i) => {
    const added = b.isFuture ? 0 : periodAdds[i];
    if (!b.isFuture) runningTotal += added;
    return {
      label: b.label,
      periodStart: b.key,
      total: runningTotal,
      added,
      isWeekStart: b.isDivider,
      isFuture: b.isFuture,
    };
  });
}

function buildDailyCumulativeSubscriberTrend(
  createdAtValues: string[],
  dayCount: number = DEFAULT_CONNECTION_TREND_DAYS,
): CumulativeSubscriberTrendPoint[] {
  const now = new Date();
  const todayKey = localDayKeyFromDate(startOfLocalDay(now));
  const windowStart = startOfTrendWindow(dayCount);
  const windowStartKey = localDayKeyFromDate(windowStart);

  let runningTotal = 0;
  const dailyAdds = new Map<string, number>();

  for (const raw of createdAtValues) {
    const key = createdAtDayKey(raw);
    if (!key) continue;
    if (key < windowStartKey) {
      runningTotal += 1;
    } else if (key <= todayKey) {
      dailyAdds.set(key, (dailyAdds.get(key) ?? 0) + 1);
    }
  }

  const buckets: CumulativeSubscriberTrendPoint[] = [];
  // dayCount days of history ending today, plus 1 day into the future
  for (let i = dayCount - 1; i >= -1; i--) {
    const d = startOfLocalDay(now);
    d.setDate(d.getDate() - i);
    const key = localDayKeyFromDate(d);
    const isFuture = key > todayKey;
    const added = isFuture ? 0 : (dailyAdds.get(key) ?? 0);
    if (!isFuture) {
      runningTotal += added;
    }
    buckets.push({
      label: formatDayLabel(key),
      periodStart: key,
      total: runningTotal,
      added,
      isWeekStart: isMonday(key),
      isFuture,
    });
  }

  return buckets;
}

function aggregateCumulativeSubscriberByWeek(
  daily: CumulativeSubscriberTrendPoint[],
): CumulativeSubscriberTrendPoint[] {
  const weeks = new Map<string, { added: number; total: number }>();

  for (const point of daily) {
    if (point.isFuture) continue;
    const weekKey = mondayWeekKey(point.periodStart);
    const existing = weeks.get(weekKey);
    if (existing) {
      existing.added += point.added;
      existing.total = point.total;
    } else {
      weeks.set(weekKey, { added: point.added, total: point.total });
    }
  }

  return [...weeks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([weekKey, week]) => ({
      label: formatWeekRangeLabel(weekKey),
      periodStart: weekKey,
      total: week.total,
      added: week.added,
      isWeekStart: true,
      isFuture: false,
    }));
}

function localDayKeyFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function localTodayKey(): string {
  return localDayKeyFromDate(startOfLocalDay(new Date()));
}

export function currentTrendPeriodKey(range: ConnectionTrendDayOption): string {
  const mode = getTrendBucketMode(range);
  if (mode === '5min') return localFiveMinKeyFromDate(new Date());
  if (mode === 'hourly') return localHourKeyFromDate(new Date());
  if (mode === 'weekly') return mondayWeekKey(localTodayKey());
  return localTodayKey();
}

export function findCurrentPeriodIndex<T extends { periodStart: string; isFuture?: boolean }>(
  points: T[],
  range: ConnectionTrendDayOption,
): number | null {
  if (points.length === 0) return null;

  const targetKey = currentTrendPeriodKey(range);
  const exactIdx = points.findIndex((p) => p.periodStart === targetKey && !p.isFuture);
  if (exactIdx >= 0) return exactIdx;

  for (let i = points.length - 1; i >= 0; i--) {
    if (!points[i].isFuture) return i;
  }

  return null;
}

export function chartXAtPeriodIndex(
  index: number,
  pointCount: number,
  chartWidth: number,
): number {
  if (pointCount <= 0) return 0;
  if (pointCount <= 1) return chartWidth / 2;
  return (index / (pointCount - 1)) * chartWidth;
}

export async function fetchConnectionDashboardData(): Promise<ConnectionDashboardData> {
  const sevenDayWindowStart = startOfTrendWindow(7);

  const { data, error, count } = await supabase
    .from('subscribers')
    .select(
      'id, email, name, role, profile_score, profile_score_tier, created_at, email_status',
      { count: 'exact' },
    )
    .order('created_at', { ascending: false, nullsFirst: false })
    .limit(20000);

  if (error) {
    throw new Error(error.message || 'Failed to load subscribers for dashboard');
  }

  const rows = (data ?? []) as SubscriberDashboardRow[];
  const total = count ?? rows.length;
  const recent = rows.slice(0, 5) as SubscriberRow[];

  const createdAtValues = rows
    .map((row) => (typeof row.created_at === 'string' ? row.created_at : ''))
    .filter(Boolean);

  let addedLast7Days = 0;
  for (const raw of createdAtValues) {
    if (isCreatedOnOrAfterDay(raw, sevenDayWindowStart)) addedLast7Days += 1;
  }

  return {
    total,
    addedLast7Days,
    confirmed: summarizeByEmailStatus(rows, sevenDayWindowStart, 'confirmed'),
    recent,
    createdAtValues,
  };
}
